import { ref, computed, watch, onBeforeUnmount, type Ref, type ComputedRef } from 'vue';

/**
 * Drives the matter nav's "Recents" sidebar: a drag-resizable, collapsible
 * panel. Its width persists across reloads via localStorage, but its collapsed
 * state does not — instead it's derived from how many side panels (references,
 * raw view) are open, so the rail steps aside automatically when the screen
 * gets crowded. The user can still toggle to override, but that override is
 * cleared the next time panel visibility changes, handing control back to the
 * automatic behavior. Collapsing swaps the panel for a slim vertical
 * "Dialogues" rail (rendered in MatterPage) that re-opens it on click; the
 * far-left icon rail stays visible throughout.
 */

const WIDTH_KEY = 'pepper.nav.sidebarWidth';

export const SIDEBAR_MIN_WIDTH = 220;
export const SIDEBAR_MAX_WIDTH = 640;
const SIDEBAR_DEFAULT_WIDTH = 280;

// Module-level "please expand" signal, shared across every composable instance.
// The far-left nav rail bumps it (via `requestSidebarExpand`) when a section is
// clicked; each mounted instance watches it and forces its panel open. Lives
// outside the factory so the rail — a sibling of the sidebar host, not its
// parent — can drive the host's collapse state without prop plumbing.
const expandRequest = ref(0);

/** Ask the active section's sidebar to expand (used by the nav rail). */
export function requestSidebarExpand(): void {
    expandRequest.value += 1;
}

// Auto-collapse the nav rail once this many side panels are open. With one or
// more data panels competing for horizontal space, the dialogue list yields.
const AUTO_COLLAPSE_PANEL_THRESHOLD = 1;

const clampWidth = (w: number): number =>
    Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, w));

function readWidth(): number {
    try {
        const raw = localStorage.getItem(WIDTH_KEY);
        const parsed = raw === null ? NaN : Number(raw);
        return Number.isFinite(parsed) ? clampWidth(parsed) : SIDEBAR_DEFAULT_WIDTH;
    } catch {
        return SIDEBAR_DEFAULT_WIDTH;
    }
}

/**
 * @param openPanelCount getter for the number of side panels (references, raw
 *   view) currently open. Drives the automatic collapse/expand behavior.
 */
export function useResizableSidebar(
    openPanelCount: () => number = () => 0,
): {
    width: Ref<number>;
    collapsed: ComputedRef<boolean>;
    startResize: (event: MouseEvent) => void;
    toggleCollapsed: () => void;
} {
    const width = ref(readWidth());

    // Automatic state from panel pressure; `override` (when non-null) wins until
    // the next panel-visibility change resets it back to null.
    const autoCollapsed = computed(() => openPanelCount() >= AUTO_COLLAPSE_PANEL_THRESHOLD);
    const override = ref<boolean | null>(null);
    const collapsed = computed(() =>
        override.value === null ? autoCollapsed.value : override.value,
    );

    watch(width, (w) => {
        try {
            localStorage.setItem(WIDTH_KEY, String(Math.round(w)));
        } catch {
            /* best-effort — resize still works without persistence */
        }
    });

    // A change in how many panels are open supersedes the user's manual choice.
    watch(openPanelCount, () => {
        override.value = null;
    });

    // The nav rail asks the active section to expand (e.g. clicking Narratives
    // while its sidebar is collapsed). Force the panel open via the override;
    // it's cleared again on the next panel-visibility change, as usual.
    watch(expandRequest, () => {
        override.value = false;
    });

    let startX = 0;
    let startWidth = 0;

    const onMove = (event: MouseEvent) => {
        width.value = clampWidth(startWidth + (event.clientX - startX));
    };

    const endResize = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', endResize);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    };

    const startResize = (event: MouseEvent) => {
        if (collapsed.value) return;
        event.preventDefault();
        startX = event.clientX;
        startWidth = width.value;
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', endResize);
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    };

    const toggleCollapsed = () => {
        override.value = !collapsed.value;
    };

    onBeforeUnmount(endResize);

    return { width, collapsed, startResize, toggleCollapsed };
}
